'use client';

import Link from 'next/link';
import { FC } from 'react';
import { is } from 'zod/v4/locales';

import { round } from '../services/math';
import Img from './Image';
import ProductRibbon from './ProductRibbon';

type Sale = {
  name: string;
  description: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  currency?: string;
  start_date?: string | null;
  end_date?: string | null;
  validfrom?: string;
  validto?: string;
};

type Picture = {
  url?: string;
};

type PriceVariant = {
  value: number;
  currency?: string;
  factor?: number;
};

type Price = {
  public?: PriceVariant;
  franchise?: PriceVariant;
  wholesaler?: PriceVariant;
};

type QuantityVariant = {
  value: number;
  location?: number;
};

type Quantity = {
  initial?: QuantityVariant;
  stock?: QuantityVariant;
};

export type ProductType = {
  id?: string;
  name?: string;
  description?: string;
  picture?: Picture;
  gallery?: Picture[];
  price?: Price;
  quantity?: Quantity;
  sale?: Sale;
  featured?: boolean;
};

type User = {
  username?: string;
  email?: string;
};

const ARS = (value: number) => {
  return Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(round(value, 0));
};

export type ProductCartProps = {
  isNewIn?: boolean;
  product?: ProductType;
  className?: string;
  callbackUrl?: string;
  user?: User;
  price?: number;
  priceResolver?: (product: ProductType, user: User) => number;
  size?: 'sm' | 'md' | 'lg' | 'full';
  lowStockThreshold?: number;
  inOnSale?: boolean;
  href?: string;
};

const LowStockGradient = ({ quantity }: { quantity?: Quantity }) => {
  return (
    <div className="absolute bottom-0 left-0 right-0 z-10">
      <div className="bg-linear-to-t from-amber-600 via-amber-500 to-transparent backdrop-blur-sm- py-3 px-2">
        <div className="flex items-center justify-center gap-2 animate-pulse">
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-white font-bold text-xs uppercase tracking-wide drop-shadow-lg">
            {quantity?.stock?.value === 1 ? '¡Última unidad!' : `¡Solo ${quantity?.stock?.value} disponibles!`}
          </span>
          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

const ProductCard: FC<ProductCartProps> = (props) => {
  const { isNewIn, className, callbackUrl, product, size = 'md', lowStockThreshold = 3, inOnSale, href } = props;
  const callBackUrl = callbackUrl || href || '#';

  if (!product) return null;

  const { name, picture, gallery, price_public_value, stock_reserved: _sr, sale: _saleDirect, featured, sales, on_sale, effective_sale } = product as any;
  // Resolve effective sale: direct field → pivot array → backend-enriched effective_sale
  const sale = _saleDirect ?? sales?.[0]?.sale ?? effective_sale ?? null;
  const image = picture?.url || gallery?.[0]?.url || '/placeholder.webp';

  if (!image) return null;

  if (!price_public_value) return null;

  const stock_reserved = 10; //_sr || 0;

  const isLowStock = false; //stock_reserved !== undefined && stock_reserved < lowStockThreshold;

  const sizeClasses = {
    sm: { figure: 'w-48 h-64', img: { width: 192, height: 256 } },
    md: { figure: 'w-72 h-96', img: { width: 320, height: 480 } },
    lg: { figure: 'w-80 h-[30rem]', img: { width: 480, height: 720 } },
    full: { figure: 'w-full h-auto', img: { width: 1024, height: 768 } },
  };

  const currentSize = sizeClasses[size];

  // Calculate sale prices and ribbon label
  const originalPrice = Number(price_public_value || 0);
  let discountedPrice = originalPrice;
  let saleRibbonLabel = 'SALE';
  let hasSale = false;
  // BUY_X_GET_Y is a quantity-based promotion: no per-unit price reduction shown
  let isBuyXGetY = false;

  if (sale && sale.discount_value > 0) {
    hasSale = true;
    if (sale.discount_type === 'PERCENTAGE') {
      discountedPrice = originalPrice * (1 - sale.discount_value / 100);
      saleRibbonLabel = `-${Math.round(sale.discount_value)}%`;
    } else if (sale.discount_type === 'FIXED') {
      discountedPrice = originalPrice - sale.discount_value;
      saleRibbonLabel = `-${ARS(sale.discount_value)}`;
    } else if (sale.discount_type === 'BUY_X_GET_Y') {
      isBuyXGetY = true;
      // discount_value = X: buy X, get 1 free
      const buyX = Math.max(1, Math.floor(Number(sale.discount_value) || 1));
      saleRibbonLabel = `${buyX + 1} x ${buyX}`;
      // No price reduction per unit — price stays at originalPrice
      discountedPrice = originalPrice;
    }
    // Ensure discounted price doesn't go below 0
    discountedPrice = Math.max(0, discountedPrice);
  }

  // For BUY_X_GET_Y: display the regular price (no per-unit discount shown)
  const displayPrice = hasSale && !isBuyXGetY ? discountedPrice : originalPrice;

  return (
    <div data-testid="product-card" className={`${className} flex flex-col gap-3 relative`}>
      <Link href={callBackUrl || '#'} data-testid="product-card-link" className="flex justify-center relative">
        <figure className={`${currentSize.figure} relative overflow-hidden bg-surface`}>
          {isNewIn && <ProductRibbon label="New In" variant="secondary" position="top-left" />}
          {isLowStock && <ProductRibbon label={stock_reserved <= 2 ? '¡Última unidad!' : `¡Solo ${stock_reserved} disponibles!`} variant="primary" position="bottom-left" size="md" />}
          {(inOnSale || hasSale || featured || on_sale) && <ProductRibbon label={saleRibbonLabel} size="md" variant="error" />}
          {image && <Img alt={name as string} src={image} dimensions={currentSize.img} className=" h-auto" priority />}
        </figure>
      </Link>

      <Link href={callBackUrl || '#'}>
        <div className="text-sm">
          <div className="p-1 text-text text-sm uppercase">{name}</div>
          <div className="flex items-center gap-2">
            {hasSale && !isBuyXGetY && (
              <span className="text-textSecondary text-sm decoration-textSecondary" style={{ textDecoration: 'line-through' }}>
                {ARS(originalPrice)}
              </span>
            )}
            <span className={`font-bold ${hasSale ? 'text-primary' : 'text-text'}`}>{ARS(displayPrice)}</span>
          </div>
        </div>
        <div className="flex flex-row items-center justify-between gap-2 text-[0.7rem] text-textSecondary">
          <div>Precio sin impuestos nacionales</div>
          <div>{ARS(displayPrice / 1.21)}</div>
        </div>
      </Link>
    </div>
  );
};

export default ProductCard;
